import { useState } from 'react';
import classNames from 'classnames';

import { CloseIcon } from '../../icons/ui';
import styles from './_index.module.scss';

export interface AnnouncementProps extends React.HTMLAttributes<HTMLDivElement> {
  callToAction?: React.ReactNode;
  closeButtonDataTestId?: string;
  elevation?: 1 | 2;
  footer?: React.ReactNode;
  hasClose?: boolean;
  header?: React.ReactNode;
  icon?: React.ReactNode;
  isBold?: boolean;
  isCentered?: boolean;
  text?: React.ReactNode | string;
  variant?: 'ai' | 'error' | 'feedback' | 'info' | 'recommendation' | 'success' | 'warn';
}

const Announcement = ({
  callToAction,
  className,
  closeButtonDataTestId,
  elevation,
  footer,
  hasClose,
  header,
  icon,
  isBold,
  isCentered,
  text = 'Announcement',
  variant = 'success',
  ...props
}: AnnouncementProps) => {
  const [isActive, setIsActive] = useState(true);

  if (!isActive) return null;

  return (
    <div
      className={classNames(
        styles[`announcement-${variant}`],
        styles[`is-elevation-${elevation}`],
        className,
        {
          [styles['is-bold']]: isBold,
          [styles['is-centered']]: isCentered,
        }
      )}
      role="status"
      {...props}
    >
      <div>
        {icon && (
          <div
            aria-hidden
            className={classNames(
              styles['announcement-icon'],
              styles[`announcement-icon-${variant}`]
            )}
          >
            {icon}
          </div>
        )}
        <div className={styles['announcement-content']}>
          {header && <h3>{header}</h3>}
          <div>{typeof text === 'object' ? text : <p>{text}</p>}</div>
          {callToAction && <div>{callToAction}</div>}
        </div>
        {hasClose && (
          <div className={styles['announcement-close']}>
            <button
              aria-label="Close"
              data-testid={closeButtonDataTestId}
              onClick={() => setIsActive(false)}
            >
              <CloseIcon />
            </button>
          </div>
        )}
      </div>
      {footer && (
        <div
          className={classNames(
            styles['announcement-footer'],
            styles[`announcement-footer-${variant}`]
          )}
        >
          {footer}
        </div>
      )}
    </div>
  );
};

export default Announcement;
